<template>
  <div v-if="labelsToRender.length || badgesToRender.length">
    <template v-if="badgesToRender.length">
      <ProductFeatureBadge :badges="badgesToRender" />
    </template>

    <template v-else-if="labelsToRender.length">
      <img
        v-for="label in labelsToRender"
        :key="`${label.featureId}-${label.featureValue}`"
        :src="label.imageSrc"
        :width="label.width"
        :height="label.height"
        :alt="label.alt || ''"
        :class="[label.class]"
        loading="lazy"
        decoding="async"
        class="feature-label"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ProductFeatureBadge from '~/components/ProductFeatureBadge.vue';
import getDateByDaysAgo from '~/helpers/getDateByDaysAgo';
import type { Product } from './ProductCard.vue';

interface Label {
  featureId: string;
  featureValue: string | number;
  width?: number;
  height?: number;
  alt?: string;
  class?: string;
  imageSrc?: string;
  isActive: (product: Product) => boolean;
}

interface Badge {
  featureId: string;
  featureValue: () => any;
  color: string;
  text: string;
  isActive: (product: Product) => boolean;
}

interface Props {
  product: Product;
}

const props = defineProps<Props>();
const { t } = useI18n();

const PRODUCT_STATUSES = {
  archive: 8,
};

const AVAILABLE_BADGES: Badge[] = [
  {
    featureId: 'ARCHIVE_STATUS',
    featureValue: () => PRODUCT_STATUSES.archive,
    color: 'var(--ab-canyon-color-accent-4)',
    text: t('archive_status_text'),
    isActive(product: Product) {
      return product.status === this.featureValue();
    },
  },
  {
    featureId: 'NOVELTY_STATUS',
    featureValue: () => getDateByDaysAgo(90),
    color: 'var(--ab-canyon-color-accent-3)',
    text: t('novelty_status_text'),
    isActive(product: Product) {
      if (product.status === PRODUCT_STATUSES.archive) return false;
      return product.external_cf_published_at
        ? new Date(product.external_cf_published_at) >= this.featureValue()
        : false;
    },
  },
];

const AVAILABLE_LABELS: Label[] = [
  {
    featureId: 'PRODUCT_SERIES',
    featureValue: 'Hexagon',
    width: 120,
    height: 67,
    alt: 'hexagon-label',
    class: 'feature-label hexagon-label',
    imageSrc: '/img/product-features/hexagon-2x.png',
    isActive(product: Product) {
      return product.feature_groups?.some((featureGroup) =>
        featureGroup.features.some(
          (feature) =>
            feature.external_cf_id === this.featureId &&
            feature.value === this.featureValue,
        ),
      );
    },
  },
];

const getItemsToRender = (
  product: Product,
  availableItems: Label[] | Badge[],
) => {
  return availableItems.filter((item) => item.isActive(product));
};

const badgesToRender = computed(
  () => getItemsToRender(props.product, AVAILABLE_BADGES) as Badge[],
);

const labelsToRender = computed(
  () => getItemsToRender(props.product, AVAILABLE_LABELS) as Label[],
);
</script>

<style lang="scss">
.feature-label {
  position: absolute;
  z-index: 11;

  &.hexagon-label {
    left: 0;
    top: 0;
  }
}
</style>
